import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  Home, 
  Bath, 
  Bed, 
  Square, 
  MapPin, 
  Calendar,
  Phone,
  Mail,
  Loader2,
  Car,
  Trees,
  Thermometer,
  Wind,
  School,
  DollarSign,
  Building,
  ChevronLeft,
  ChevronRight,
  X,
  CalendarDays
} from "lucide-react";
import { PropertyContactModal } from "../components/PropertyContactModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatPrice = (price) => {
  if (!price) return "Price TBD";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);
};

const FeatureSection = ({ title, icon: Icon, items }) => {
  if (!items || items.length === 0) return null;
  
  return (
    <div className="mb-6">
      <h4 className="flex items-center gap-2 text-[#0F172A] font-semibold mb-2">
        <Icon size={18} className="text-[#D4AF37]" />
        {title}
      </h4>
      <div className="flex flex-wrap gap-2">
        {items.map((item, index) => (
          <span 
            key={index}
            className="bg-[#F8FAFC] text-[#475569] px-3 py-1 text-sm"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

export default function PropertyDetail() {
  const { listingKey } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get(`${API}/listings/${listingKey}`);
        setListing(response.data);
      } catch (err) {
        console.error("Error fetching listing:", err);
        setError("Unable to load this listing. It may no longer be available.");
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
    window.scrollTo(0, 0);
  }, [listingKey]);

  const placeholderImage = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&q=80";
  const images = listing?.photos?.length > 0 
    ? listing.photos.map(p => p.MediaURL) 
    : [placeholderImage];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-[#475569]">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="bg-[#0F172A] py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <Link 
              to="/listings" 
              className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Listings
            </Link>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <Home className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Property Not Found</h2>
          <p className="text-[#475569] mb-6">{error}</p>
          <Link to="/listings" className="btn-primary">
            Browse All Listings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F172A] py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/listings" 
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Listings
          </Link>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="relative h-[50vh] md:h-[60vh] bg-[#0F172A] cursor-pointer" onClick={() => setShowGallery(true)}>
        <img
          src={images[currentImageIndex]}
          alt={`${listing.address} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover"
        />
        
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft size={24} className="text-[#0F172A]" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
              aria-label="Next image"
            >
              <ChevronRight size={24} className="text-[#0F172A]" />
            </button>
          </>
        )}

        {/* Photo Count */}
        <button 
          onClick={() => setShowGallery(true)}
          className="absolute bottom-4 right-4 bg-[#0F172A]/80 text-white px-4 py-2 text-sm hover:bg-[#0F172A] transition-colors flex items-center gap-2"
        >
          <Square size={16} />
          View All {images.length} Photos
        </button>

        {/* Status Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-4 py-2 text-sm font-semibold ${
            listing.status === 'Active' 
              ? 'bg-green-500 text-white' 
              : listing.status === 'Pending'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-500 text-white'
          }`}>
            {listing.status || 'Unknown'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Price & Address */}
            <div className="mb-8">
              <span className="text-4xl md:text-5xl font-bold text-[#D4AF37] font-serif">
                {formatPrice(listing.price)}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] mt-4 font-serif">
                {listing.address}
              </h1>
              <p className="text-[#475569] flex items-center gap-2 mt-2 text-lg">
                <MapPin size={18} className="text-[#D4AF37]" />
                {listing.city}, {listing.state} {listing.zipCode}
                {listing.county && ` • ${listing.county} County`}
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 p-6 bg-white shadow-md">
              {listing.beds && (
                <div className="text-center p-4">
                  <Bed size={28} className="text-[#D4AF37] mx-auto mb-2" />
                  <span className="text-2xl font-bold text-[#0F172A] block">{listing.beds}</span>
                  <span className="text-[#475569] text-sm">Bedrooms</span>
                </div>
              )}
              {listing.baths && (
                <div className="text-center p-4">
                  <Bath size={28} className="text-[#D4AF37] mx-auto mb-2" />
                  <span className="text-2xl font-bold text-[#0F172A] block">{listing.baths}</span>
                  <span className="text-[#475569] text-sm">Bathrooms</span>
                </div>
              )}
              {listing.sqft && (
                <div className="text-center p-4">
                  <Square size={28} className="text-[#D4AF37] mx-auto mb-2" />
                  <span className="text-2xl font-bold text-[#0F172A] block">{listing.sqft.toLocaleString()}</span>
                  <span className="text-[#475569] text-sm">Sq Ft</span>
                </div>
              )}
              {listing.yearBuilt && (
                <div className="text-center p-4">
                  <Calendar size={28} className="text-[#D4AF37] mx-auto mb-2" />
                  <span className="text-2xl font-bold text-[#0F172A] block">{listing.yearBuilt}</span>
                  <span className="text-[#475569] text-sm">Year Built</span>
                </div>
              )}
            </div>

            {/* Description */}
            {listing.description && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-[#0F172A] font-serif mb-4">
                  About This Property
                </h3>
                <p className="text-[#475569] leading-relaxed whitespace-pre-line">
                  {listing.description}
                </p>
              </div>
            )}

            {/* Features */}
            <div className="bg-white p-6 shadow-md mb-8">
              <h3 className="text-xl font-bold text-[#0F172A] font-serif mb-6">
                Property Features
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FeatureSection title="Heating" icon={Thermometer} items={listing.features?.heating} />
                <FeatureSection title="Cooling" icon={Wind} items={listing.features?.cooling} />
                <FeatureSection title="Interior" icon={Home} items={listing.features?.interior} />
                <FeatureSection title="Exterior" icon={Trees} items={listing.features?.exterior} />
                <FeatureSection title="Parking" icon={Car} items={listing.features?.parking} />
                <FeatureSection title="Patio & Porch" icon={Home} items={listing.features?.patio} />
                <FeatureSection title="Flooring" icon={Square} items={listing.features?.flooring} />
                <FeatureSection title="Appliances" icon={Home} items={listing.features?.appliances} />
              </div>

              {listing.architecturalStyle?.length > 0 && (
                <FeatureSection title="Architectural Style" icon={Building} items={listing.architecturalStyle} />
              )}
            </div>

            {/* Schools */}
            {(listing.schools?.elementary || listing.schools?.middle || listing.schools?.high) && (
              <div className="bg-white p-6 shadow-md mb-8">
                <h3 className="text-xl font-bold text-[#0F172A] font-serif mb-4 flex items-center gap-2">
                  <School size={20} className="text-[#D4AF37]" />
                  Schools
                </h3>
                <div className="space-y-2">
                  {listing.schools.elementary && (
                    <p className="text-[#475569]"><span className="font-medium">Elementary:</span> {listing.schools.elementary}</p>
                  )}
                  {listing.schools.middle && (
                    <p className="text-[#475569]"><span className="font-medium">Middle:</span> {listing.schools.middle}</p>
                  )}
                  {listing.schools.high && (
                    <p className="text-[#475569]"><span className="font-medium">High:</span> {listing.schools.high}</p>
                  )}
                </div>
              </div>
            )}

            {/* Additional Details */}
            <div className="bg-white p-6 shadow-md">
              <h3 className="text-xl font-bold text-[#0F172A] font-serif mb-4">
                Additional Details
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {listing.propertyType && (
                  <div>
                    <span className="text-[#94A3B8]">Property Type</span>
                    <p className="text-[#0F172A] font-medium">{listing.propertyType}</p>
                  </div>
                )}
                {listing.lotSize && (
                  <div>
                    <span className="text-[#94A3B8]">Lot Size</span>
                    <p className="text-[#0F172A] font-medium">
                      {listing.lotSize} {listing.lotSizeUnits || 'Sq Ft'}
                    </p>
                  </div>
                )}
                {listing.garage?.spaces && (
                  <div>
                    <span className="text-[#94A3B8]">Garage</span>
                    <p className="text-[#0F172A] font-medium">
                      {listing.garage.spaces} {listing.garage.spaces === 1 ? 'Space' : 'Spaces'}
                    </p>
                  </div>
                )}
                {listing.hoa?.fee && (
                  <div>
                    <span className="text-[#94A3B8]">HOA Fee</span>
                    <p className="text-[#0F172A] font-medium">
                      {formatPrice(listing.hoa.fee)} {listing.hoa.feeFrequency || ''}
                    </p>
                  </div>
                )}
                {listing.taxInfo?.annualAmount && (
                  <div>
                    <span className="text-[#94A3B8]">Annual Taxes</span>
                    <p className="text-[#0F172A] font-medium">{formatPrice(listing.taxInfo.annualAmount)}</p>
                  </div>
                )}
                {listing.daysOnMarket && (
                  <div>
                    <span className="text-[#94A3B8]">Days on Market</span>
                    <p className="text-[#0F172A] font-medium">{listing.daysOnMarket} days</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Contact Card */}
            <div className="bg-white p-6 shadow-md sticky top-6">
              <h3 className="text-xl font-bold text-[#0F172A] font-serif mb-4">
                Interested in this property?
              </h3>
              
              {listing.coListingAgent?.firstName && (
                <div className="mb-6 pb-6 border-b border-gray-100">
                  <p className="text-sm text-[#94A3B8] mb-1">Listed by</p>
                  <p className="text-lg font-semibold text-[#0F172A]">
                    {listing.coListingAgent.firstName} {listing.coListingAgent.lastName}
                  </p>
                  {listing.coListingAgent.phone && (
                    <a 
                      href={`tel:${listing.coListingAgent.phone}`}
                      className="text-[#D4AF37] hover:underline flex items-center gap-2 mt-2"
                    >
                      <Phone size={16} />
                      {listing.coListingAgent.phone}
                    </a>
                  )}
                  {listing.coListingAgent.email && (
                    <a 
                      href={`mailto:${listing.coListingAgent.email}`}
                      className="text-[#D4AF37] hover:underline flex items-center gap-2 mt-1"
                    >
                      <Mail size={16} />
                      {listing.coListingAgent.email}
                    </a>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <a 
                  href="tel:919-879-5636" 
                  className="btn-primary w-full justify-center"
                >
                  <Phone size={18} />
                  Call (919) 879-5636
                </a>
                <button 
                  onClick={() => setShowContactModal(true)}
                  className="btn-outline w-full justify-center"
                >
                  <Mail size={18} />
                  Send a Message
                </button>
              </div>

              <p className="text-sm text-[#94A3B8] text-center mt-4">
                We'll respond within 24 hours
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#0F172A] py-8 px-6 text-center">
        <p className="text-[#94A3B8] text-sm">
          © {new Date().getFullYear()} Next Chapter Moves NC. All rights reserved.
        </p>
      </div>

      {/* Contact Modal */}
      <PropertyContactModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)}
        property={listing}
      />

      {/* Full Screen Gallery */}
      {showGallery && (
        <div className="fixed inset-0 bg-[#0F172A] z-[100] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <span className="text-white">
              {currentImageIndex + 1} / {images.length} Photos
            </span>
            <button
              onClick={() => setShowGallery(false)}
              className="text-white hover:text-[#D4AF37] transition-colors"
            >
              <X size={28} />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center relative">
            <img
              src={images[currentImageIndex]}
              alt={`Photo ${currentImageIndex + 1}`}
              className="max-h-full max-w-full object-contain"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 w-12 h-12 bg-white/20 hover:bg-white/40 flex items-center justify-center transition-colors"
                >
                  <ChevronLeft size={28} className="text-white" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 w-12 h-12 bg-white/20 hover:bg-white/40 flex items-center justify-center transition-colors"
                >
                  <ChevronRight size={28} className="text-white" />
                </button>
              </>
            )}
          </div>
          {/* Thumbnails */}
          <div className="p-4 border-t border-white/10 overflow-x-auto">
            <div className="flex gap-2">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-16 overflow-hidden ${
                    index === currentImageIndex ? 'ring-2 ring-[#D4AF37]' : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`Thumb ${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
